// THE attribute-schema `ui_hints` reader — one parser, every consumer.
//
// `attribute_schemas.ui_hints` is produced in exactly one place,
// `packages/verticals/src/attribute-transform.ts` (D-056 moved it there so the
// pack installer and the seed factory could not drift). Its shape is:
//
//   {
//     transform_version: 1,
//     applies_to: "…",
//     labels: { en: "Cable, power", ru: "Кабель силовой", … },  ← THE SCHEMA's name
//     order:  ["conductor_material", "cross_section_mm2", …],
//     fields: {
//       cross_section_mm2: {
//         labels:    { en: "Cross-section", ru: "Сечение", … },  ← THE FIELD's name
//         unit_code: "mm2",
//       },
//       …
//     }
//   }
//
// WHY THIS FILE EXISTS. Two clients each hand-rolled a private parser and both
// made the same two mistakes: they read the per-FIELD label out of `labels[key]`
// — which is the SCHEMA's own locale map, so it never matches a field key and
// always fell through to the raw key — and they read units out of `units[key]`,
// a node that has never existed in the produced shape. The visible result was
// `conductor_material` / `cross_section_mm2` as form labels and a row of
// unit-less numbers as list chips, identically in every locale, on the two
// most-used screens in the module. Module 01 §4 asks for "attribute chips
// (mm², colour swatch)"; the mm² was absent everywhere.
//
// One shape, read in one place, consumed by the schema form and the list chips.
// A second copy of this logic is the defect, so there is only ever one.
//
// Locale resolution matches `localizedName` (floor-pwa/lib/production-floor):
// the ACTIVE locale's primary subtag, then `en`, then the raw key — a factory
// user never sees an empty label, and a pack that has not translated a field
// yet degrades to English rather than to snake_case.

import { normalizeLocale } from "@assessly/contracts/locales";

/** The locale-resolved, per-field view of a schema's `ui_hints`. */
export interface AttrHints {
  /** Pack field order — the order the form and the chips render in. */
  order: string[];
  /** key → the field's label in the active locale (never blank). */
  labels: Record<string, string>;
  /** key → the field's `unit_code` ("mm2"), when the pack declared one. */
  units: Record<string, string>;
  /** Keys the schema marks as colour swatches (forward-compatible; the chip
   * component also treats a key that READS as a colour as one). */
  swatches: Set<string>;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * Resolve one field's label from its `labels` locale map. The active
 * language through the registry (`ru-KZ` → `ru`, `zh-TW` → `zh-Hant`, never
 * a two-letter cut that reads Traditional as Simplified) → `en` → the key.
 */
export function fieldLabel(
  labels: Record<string, unknown> | undefined,
  locale: string,
  key: string,
): string {
  if (labels === undefined) return key;
  const short = normalizeLocale(locale) ?? locale;
  const localized = labels[short];
  if (typeof localized === "string" && localized.length > 0) return localized;
  const english = labels["en"];
  if (typeof english === "string" && english.length > 0) return english;
  return key;
}

/**
 * Parse an attribute schema's `ui_hints` for `locale`. Total: a missing,
 * malformed or empty hints object yields empty maps and every consumer falls
 * back to the raw key, which is exactly what an unhinted schema should do.
 */
export function parseUiHints(
  uiHints: Record<string, unknown> | undefined,
  locale: string,
): AttrHints {
  const hints = asRecord(uiHints) ?? {};
  const fields = asRecord(hints["fields"]) ?? {};

  const labels: Record<string, string> = {};
  const units: Record<string, string> = {};
  for (const [key, raw] of Object.entries(fields)) {
    const field = asRecord(raw);
    if (field === undefined) continue;
    labels[key] = fieldLabel(asRecord(field["labels"]), locale, key);
    const unit = field["unit_code"];
    // `unit_code` is `string | null` in the pack schema — a dimensionless field
    // (conductor_material) carries null and must render NO unit, not "null".
    if (typeof unit === "string" && unit.length > 0) units[key] = unit;
  }

  return {
    order: asStringArray(hints["order"]),
    labels,
    units,
    swatches: new Set(asStringArray(hints["swatches"])),
  };
}
